// Jeden stan sesji dla całej aplikacji: `me` (GET /api/auth/me) + `config`
// (GET /api/auth/config → flaga AUTH_REQUIRED), oba pobierane RAZ na start przez
// <SessionProvider> (components/auth/SessionProvider.tsx), który owija AppShell.
// Strony w powłoce (/konto, /admin, Squad planner, przyszłe kontrolki edycji)
// czytają je przez `useSession()` / `useCan()` zamiast własnego `useMe()` —
// inaczej wylogowanie na /konto nie dotarłoby do menu w górnej belce.
//
// Trasy /print stoją POZA AppShell, więc poza providerem: dostają stan domyślny
// (flaga wyłączona, `useCan` = true) — PDF rysuje się jak dziś.

import { createContext, useContext, useMemo } from 'react'

import { canView } from './access'
import { hasAccess, isAdmin, type Level, type MeState } from './auth'

export interface AuthConfig {
  // true = cała aplikacja za logowaniem i macierzą dostępu (backend AUTH_REQUIRED).
  auth_required: boolean
}

export const AUTH_CONFIG_OFF: AuthConfig = { auth_required: false }

/** GET /api/auth/config — publiczne. Brak endpointu, błąd, HTML z fallbacku SPA
 *  albo cokolwiek innego niż `true` = flaga wyłączona (aplikacja jak dawniej). */
export async function fetchAuthConfig(): Promise<AuthConfig> {
  try {
    const res = await fetch('/api/auth/config', { headers: { Accept: 'application/json' } })
    if (!res.ok) return AUTH_CONFIG_OFF
    const body = await res.json()
    return { auth_required: body?.auth_required === true }
  } catch {
    return AUTH_CONFIG_OFF
  }
}

export interface SessionState extends MeState {
  // null = jeszcze się ładuje.
  config: AuthConfig | null
  // Skrót: config?.auth_required === true.
  authRequired: boolean
}

const NO_PROVIDER: SessionState = {
  me: null,
  loading: false,
  error: null,
  setMe: () => {},
  reload: () => {},
  config: AUTH_CONFIG_OFF,
  authRequired: false,
}

export const SessionContext = createContext<SessionState>(NO_PROVIDER)

/** Bieżąca sesja i flaga. Poza providerem (trasy /print) — stan „flaga wyłączona". */
export function useSession(): SessionState {
  return useContext(SessionContext)
}

export interface AccessApi {
  // Czy bramka działa (flaga włączona). Przy false wszystko poniżej zwraca true.
  enforced: boolean
  can: (section: string, level?: Level) => boolean
  canView: (pathname: string) => boolean
}

/** Uprawnienia bieżącego konta z uwzględnieniem flagi — wersja na wiele pytań naraz
 *  (filtr zakładek, pigułek). Dla jednej kontrolki wygodniej `useCan`. */
export function useAccess(): AccessApi {
  const { me, authRequired } = useSession()
  return useMemo<AccessApi>(() => {
    if (!authRequired) return { enforced: false, can: () => true, canView: () => true }
    return {
      enforced: true,
      can: (section, level = 'view') => !!me && (isAdmin(me) || hasAccess(me, section, level)),
      canView: (pathname) => canView(me, pathname),
    }
  }, [me, authRequired])
}

/**
 * Czy bieżące konto może w sekcji `section` co najmniej `level` (Edycja ⊃ Podgląd).
 *
 * Hook pod tryb podglądu: KAŻDA kontrolka edycji (zapis, usuwanie, tagowanie,
 * komentarze → sekcja `komentarze`, oceny → `noty`) ma pytać
 * `useCan(sekcja, 'edit')` i przy false chować się albo blokować — zamiast
 * czekać na 403 z backendu. Przy wyłączonej fladze AUTH_REQUIRED zwraca ZAWSZE
 * true (dziś wszyscy mogą wszystko); przy włączonej — wg `me.access`, admin zawsze.
 */
export function useCan(section: string, level: Level = 'view'): boolean {
  return useAccess().can(section, level)
}
